/**
 * ForgeAlpha logo — CL-04 Molten Gradient icon + "ForgeAlpha" wordmark.
 * Icon on the left, text on the right.
 *
 * Usage:
 *   <Logo />                          — icon + text (default)
 *   <Logo showText={false} />         — icon only
 *   <Logo size="sm" />                — small (nav)
 *   <Logo size="lg" />                — large (auth/hero)
 */

const SIZES = {
  sm: { icon: 22, text: 'text-sm', gap: 'gap-1.5', stroke: 10 },
  md: { icon: 28, text: 'text-lg', gap: 'gap-1.5', stroke: 9 },
  lg: { icon: 40, text: 'text-2xl', gap: 'gap-2', stroke: 9 },
} as const;

export function ForgeAlphaIcon({ size = 24, className = '', strokeWidth = 9 }: { size?: number; className?: string; strokeWidth?: number }) {
  const id = `fa-grad-${size}`;
  return (
    <svg
      viewBox="5 10 90 76"
      width={size}
      height={size}
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={id} x1="50" y1="12" x2="50" y2="85" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#fbbf24" />
          <stop offset="40%" stopColor="#f97316" />
          <stop offset="100%" stopColor="#dc2626" />
        </linearGradient>
      </defs>
      <path
        d="M24 80 L24 64 C24 34 34 18 50 18 C66 18 76 34 76 64 L76 80"
        stroke={`url(#${id})`}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        fill="none"
      />
      <line
        x1="14" y1="64" x2="86" y2="64"
        stroke={`url(#${id})`}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    </svg>
  );
}

export function Logo({
  showText = true,
  size = 'md',
}: {
  showText?: boolean;
  size?: 'sm' | 'md' | 'lg';
}) {
  const s = SIZES[size];

  if (!showText) {
    return <ForgeAlphaIcon size={s.icon} strokeWidth={s.stroke} />;
  }

  return (
    <div className={`flex items-center ${s.gap}`}>
      <ForgeAlphaIcon size={s.icon} strokeWidth={s.stroke} />
      <span className={`${s.text} font-extrabold tracking-tight`}>
        <span className="text-orange">Forge</span>Alpha
      </span>
    </div>
  );
}
